import React, { useEffect, useMemo, useRef, useState } from "react"
import { graphql } from "gatsby"

import CmsPage from "@utils/cmsPage"
import { trackCtaClick } from "@utils/trackEvent"
import {
  pickCollectionNode,
  resolveFaqCollection,
  resolveProofCollection,
} from "@utils/cmsCollections"
import industrySubpageMobileStyles from "@utils/industrySubpageMobileStyles"
import { getPrototypePageSlice } from "@utils/prototypeSlices"
import { coachesSeoSections } from "../data/seoStorySections"

const prototypeSlice = getPrototypePageSlice("coaches")
const coachesExtraStyles = `${prototypeSlice.extraStyles}${industrySubpageMobileStyles}`

const coachesHeroPresets = [
  {
    url: "amaracoaches.com/work-with-me",
    org: "Amara Chen Coaching",
    logo: "linear-gradient(135deg,#a78bfa,#6366f1)",
    pkgIdx: 1,
    label: "Start 3-Month Package · $650/mo",
    email: "marcus@example.com",
    name: "Marcus Lee",
    card: "•••• •••• •••• 4242",
  },
  {
    url: "devonpark.co/strategy",
    org: "Devon Park Strategy",
    logo: "linear-gradient(135deg,#0ea5e9,#0369a1)",
    pkgIdx: 3,
    label: "Start Retainer · $1,200/mo",
    email: "sasha@brightline.co",
    name: "Sasha Mendez",
    card: "•••• •••• •••• 8210",
  },
  {
    url: "priyapatel.com/book",
    org: "Priya Patel Nutrition",
    logo: "linear-gradient(135deg,#10b981,#0d9488)",
    pkgIdx: 0,
    label: "Book Single Session · $180",
    email: "riley@oakridge.io",
    name: "Riley Tan",
    card: "•••• •••• •••• 1119",
  },
  {
    url: "northshorecoaching.com/start",
    org: "North Shore Coaching",
    logo: "linear-gradient(135deg,#f59e0b,#dc2626)",
    pkgIdx: 2,
    label: "Start Starter · $680",
    email: "avery@harborlocal.com",
    name: "Avery Lin",
    card: "•••• •••• •••• 7780",
  },
]

const packageCards = [
  { name: "Single Session", meta: "60 min · pay per call", price: "$180", suffix: "/session" },
  { name: "3-Month Package", meta: "12 sessions · billed monthly", price: "$650", suffix: "/mo" },
  { name: "Starter", meta: "4 sessions one-time", price: "$680" },
  { name: "Retainer", meta: "Unlimited voice & email", price: "$1,200", suffix: "/mo" },
]

const wait = ms => new Promise(resolve => setTimeout(resolve, ms))

const coachesFallbackTestimonials = [
  {
    quote:
      "I used to chase invoices for two weeks after every session. Now the link is in my welcome email and it's just... done.",
    customerName: "Amara Chen",
    customerInitials: "AC",
    customerRole: "Executive Coach",
    industry: "Coaches",
    brandName: "Amara Chen Coaching",
    metaOne: "Practice: Executive coaching",
    metaTwo: "Size: Solo · Brooklyn",
    metaThree: "On MoonClerk since: 2023",
    statOneValue: "24",
    statOneLabel: "Active clients",
    statTwoValue: "$141K",
    statTwoLabel: "Annualized recurring",
    statThreeValue: "4 hrs",
    statThreeLabel: "Saved per week on admin",
  },
  {
    quote:
      "Packages, retainers, one-off discovery calls - one tool, one link, one dashboard. I shut down three subscriptions after switching.",
    customerName: "Devon Park",
    customerInitials: "DP",
    customerRole: "Brand Strategist",
    industry: "Coaches",
  },
  {
    quote:
      "When a card fails, MoonClerk retries it and emails the client for me. I don't even find out - which is exactly how I want it.",
    customerName: "Priya Patel",
    customerInitials: "PP",
    customerRole: "Nutrition Consultant",
    industry: "Coaches",
  },
]

const coachesFallbackFaqItems = [
  // Handoff v3.7 §03 — agency crossover item, links the agency-software solution
  // page and the Agencies page.
  {
    question: "I also run an agency — does MoonClerk handle agency retainers too?",
    answer:
      "It does. The same recurring billing runs client retainers, so coaches who have grown into an agency do not switch tools. See <a href=\"/payment-solutions/agency-software-for-billing/\">agency software</a> or the <a href=\"/agencies/\">Agencies page</a> for how retainer billing works.",
  },
  {
    question: "Can I sell one-time sessions and recurring packages from the same page?",
    answer:
      "Yes. Your Work with me page can show discovery calls, single sessions, fixed-length packages, and open-ended retainers. One page, one dashboard, clean attribution.",
  },
  {
    question: "What happens when a client's card expires?",
    answer:
      "MoonClerk emails them 7 days before the card expires with a link to update. If the charge fails anyway, it retries on days 2, 4, and 7.",
  },
  {
    question: "Can I issue refunds or pro-rate a cancellation?",
    answer:
      "Full or partial refunds are available from your dashboard in one click. Stripe handles the underlying transaction and the client gets an email.",
  },
  {
    question: "Do you handle taxes or do I still need an accountant?",
    answer:
      "MoonClerk does not file taxes for you, but it makes tax time simpler with CSV export, annual summaries per client, and live Google Sheets sync.",
  },
  {
    question: "Does it integrate with Calendly / Acuity / Notion?",
    answer:
      "Yes, via Zapier. When a client pays, you can trigger a Notion row, Calendly scheduling link, Slack ping, or native webhook.",
  },
]

const coachesSectionOrder = {
  coaches_test_band: 10,
  coaches_packages: 20,
  coaches_seo_category: 25,
  coaches_inquiry: 30,
  coaches_seo_invoices: 35,
  coaches_ledger: 40,
  coaches_story_card: 50,
  coaches_faq: 60,
  coaches_final_cta: 70,
}

const ensureSection = (sections, section) =>
  sections.some(item => item?.sectionKey === section.sectionKey) ? sections : [...sections, section]

const CoachesHero = () => {
  const mockRef = useRef(null)
  const activeRef = useRef(true)
  const visibleRef = useRef(true)
  const [activeIndex, setActiveIndex] = useState(0)
  const [fieldValues, setFieldValues] = useState({
    email: coachesHeroPresets[0].email,
    name: coachesHeroPresets[0].name,
    card: coachesHeroPresets[0].card,
  })
  const [activeField, setActiveField] = useState(null)
  const [payPulse, setPayPulse] = useState(false)
  const [stampVisible, setStampVisible] = useState(false)
  const active = coachesHeroPresets[activeIndex]

  useEffect(() => {
    activeRef.current = true
    visibleRef.current = true

    if ("IntersectionObserver" in window && mockRef.current) {
      const observer = new IntersectionObserver(
        entries => {
          entries.forEach(entry => {
            visibleRef.current = entry.isIntersecting
          })
        },
        { threshold: 0.2 }
      )
      observer.observe(mockRef.current)

      return () => {
        activeRef.current = false
        observer.disconnect()
      }
    }

    return () => {
      activeRef.current = false
    }
  }, [])

  useEffect(() => {
    const typeInto = async (field, fullValue) => {
      setActiveField(field)
      setFieldValues(values => ({ ...values, [field]: "" }))

      const chunk = Math.max(1, Math.ceil(fullValue.length / 14))
      for (let position = 0; position < fullValue.length && activeRef.current; position += chunk) {
        const nextValue = fullValue.slice(0, Math.min(fullValue.length, position + chunk))
        setFieldValues(values => ({ ...values, [field]: nextValue }))
        await wait(45)
      }

      setActiveField(null)
    }

    const runCycle = async (preset, index) => {
      setPayPulse(false)
      setStampVisible(false)
      setActiveIndex(index)
      setFieldValues({ email: "", name: "", card: "" })
      await wait(450)
      await typeInto("email", preset.email)
      await wait(100)
      await typeInto("name", preset.name)
      await wait(100)
      await typeInto("card", preset.card)
      await wait(280)
      setPayPulse(true)
      await wait(900)
      setStampVisible(true)
      await wait(1500)
    }

    const loop = async () => {
      let index = 0

      while (activeRef.current) {
        while (activeRef.current && !visibleRef.current) {
          await wait(220)
        }

        await runCycle(coachesHeroPresets[index], index)
        index = (index + 1) % coachesHeroPresets.length
        await wait(600)
      }
    }

    loop()

    return () => {
      activeRef.current = false
    }
  }, [])

  return (
    <section className="hero-f">
      <div className="hero-f-inner">
        <div className="hero-f-left">
          <div className="eyebrow">For coaches &amp; solo freelancers</div>
          {/* Handoff v3.7 §03 — Directive H1, verbatim. */}
          <h1>
            Recurring billing software for <em>coaches,</em> powered by Stripe.
          </h1>
          <p className="lede">
            {/* Handoff v3.7 §07 — hero lede carries the internal home link. */}
            Sell coaching packages, retainers, or pay-per-session with a single{" "}
            <a href="/">MoonClerk</a> link - clients pay on the spot, and the
            recurring ones keep paying.
          </p>
          <div className="cta-row" style={{ display: "flex", gap: 12, marginBottom: 28 }}>
            <a
              href="https://app.moonclerk.com/signup?try=1"
              className="btn btn-accent btn-lg"
              onClick={() =>
                trackCtaClick({
                  label: "Set up your first package",
                  location: "coaches_hero",
                  destination: "https://app.moonclerk.com/signup?try=1",
                })
              }
            >
              Set up your first package
            </a>
          </div>
          <div className="trust-card">
            <div className="trust-stats">
              <div className="trust-stat"><div className="trust-stat-num">2,100+</div><div className="trust-stat-label">Coaches &amp; freelancers</div></div>
              <div className="trust-stat"><div className="trust-stat-num">11 min</div><div className="trust-stat-label">Avg time to first paid</div></div>
              <div className="trust-stat"><div className="trust-stat-num">94%</div><div className="trust-stat-label">Invoices paid on-time</div></div>
            </div>
            <div className="trust-badges">
              <div className="trust-badge"><span><b>No-code</b> setup</span></div>
              <div className="trust-badge"><span>Cards, ACH &amp; <b>Apple Pay</b></span></div>
            </div>
          </div>
        </div>
        <div className="hero-f-right">
          <div className="form-frame coach-mock" ref={mockRef}>
            <div className="form-frame-chrome"><span className="d" /><span className="d" /><span className="d" /><span className="u">{active.url}</span></div>
            <div className="form-frame-body">
              <div className="form-brand-row">
                <div className="form-brand-logo" style={{ background: active.logo }} />
                <div className="form-brand-meta"><div className="form-org">{active.org}</div><div className="form-kind">Choose a package</div></div>
              </div>
              <div className="pkg-grid" style={{ marginBottom: 16 }}>
                {packageCards.map((pkg, index) => (
                  <div className={`pkg-card${index === active.pkgIdx ? " on" : ""}`} key={pkg.name}>
                    <div className="pkg-name">{pkg.name}</div>
                    <div className="pkg-meta">{pkg.meta}</div>
                    <div className="pkg-price">{pkg.price}{pkg.suffix ? <span>{pkg.suffix}</span> : null}</div>
                  </div>
                ))}
              </div>
              <div className="co-field"><label htmlFor="coaches-hero-email">Email</label><input id="coaches-hero-email" className={`co-input${activeField === "email" ? " typing" : ""}`} value={fieldValues.email} readOnly tabIndex={-1} aria-hidden="true" /></div>
              <div className="co-field"><label htmlFor="coaches-hero-name">Full name</label><input id="coaches-hero-name" className={`co-input${activeField === "name" ? " typing" : ""}`} value={fieldValues.name} readOnly tabIndex={-1} aria-hidden="true" /></div>
              <div className="co-field"><label htmlFor="coaches-hero-card">Card</label><input id="coaches-hero-card" className={`co-input focus${activeField === "card" ? " typing" : ""}`} value={fieldValues.card} readOnly tabIndex={-1} aria-hidden="true" /></div>
              <button className={`co-pay is-mock-control${payPulse ? " pulse" : ""}`} type="button" style={{ background: "var(--teal)" }} aria-disabled="true" tabIndex={-1}>{active.label}</button>
              <div style={{ textAlign: "center", fontSize: 10, color: "var(--ink-3)", marginTop: 10, letterSpacing: ".06em" }}>AUTO-RENEWS · CANCEL ANY TIME · RECEIPT EMAILED</div>
              <div className={`coach-stamp${stampVisible ? " show" : ""}`}>Charged ✓</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

const CoachesPage = ({ data }) => {
  const page = data.allDatoCmsMcV2PageVertical.nodes[0] || {}
  const sectionCollection =
    page.sectionCollection ||
    pickCollectionNode(data.allDatoCmsMcV2SectionCollection.nodes, null, "coaches-sections")
  const allSections = sectionCollection.contentSections || []
  const heroMetaSection =
    allSections.find(section => section?.sectionKey === "coaches_hero_meta") || null
  const contentSections = allSections.filter(section => section?.sectionKey !== "coaches_hero_meta")
  const proofCollection =
    page.proofCollection ||
    resolveProofCollection(data.allDatoCmsMcV2ProofCollection.nodes, null, "coaches-proof")
  const faqCollection =
    page.faqCollection ||
    resolveFaqCollection(data.allDatoCmsMcV2FaqCollection.nodes, null, "coaches-faq")
  const faqItems = faqCollection.faqItems?.length ? faqCollection.faqItems : coachesFallbackFaqItems
  const testimonials = proofCollection.testimonials?.length ? proofCollection.testimonials : coachesFallbackTestimonials
  const managedContentSections = useMemo(() => {
    let sections = contentSections

    sections = ensureSection(sections, {
      sectionKey: "coaches_test_band",
      componentType: "test_band",
      hidden: false,
      headingHtml: "Used by independent coaches, consultants, and freelancers to get paid reliably",
    })
    sections = ensureSection(sections, {
      sectionKey: "coaches_story_card",
      componentType: "story_card",
      hidden: false,
      eyebrow: "Customer story",
    })
    sections = ensureSection(sections, {
      sectionKey: "coaches_faq",
      componentType: "faq_list",
      hidden: false,
      eyebrow: "FAQ",
      headingHtml: "Recurring billing software questions, <em>answered.</em>",
    })
    sections = ensureSection(sections, {
      sectionKey: "coaches_final_cta",
      componentType: "cta",
      hidden: false,
      headingHtml: "Launch recurring Stripe payments for coaching in <em>under 5 minutes.</em>",
      summary: "No contract. Take on your next client before the end of the day.",
      primaryCtaLabel: "Explore for free",
      primaryCtaHref: "https://app.moonclerk.com/signup?try=1",
    })

    // Handoff v3.7 §04 — two SEO story sections at Directive's insert points.
    coachesSeoSections.forEach(seoSection => {
      sections = ensureSection(sections, seoSection)
    })

    return sections.slice().sort((a, b) => (coachesSectionOrder[a?.sectionKey] || 999) - (coachesSectionOrder[b?.sectionKey] || 999))
  }, [contentSections])

  return (
    <CmsPage
      slug={page.slug}
      seo={page.seo}
      eyebrow={page.eyebrow}
      heading="Recurring billing software for coaches, powered by Stripe."
      summary="Sell coaching packages, retainers, or pay-per-session with a single link - clients pay on the spot, and the recurring ones keep paying."
      body={page.body}
      bodyHeading={page.bodyHeading || "For Coaches"}
      heroMediaType={page.heroMediaType}
      heroImageUrl={page.heroImageUrl}
      heroImageAlt={page.heroImageAlt}
      heroVideoUrl={page.heroVideoUrl}
      heroVideoPosterUrl={page.heroVideoPosterUrl}
      heroSlot={<CoachesHero />}
      heroMetaSection={heroMetaSection}
      contentSections={managedContentSections}
      testimonials={testimonials}
      faqItems={faqItems}
      contentHtml={page.contentHtml}
      fallbackContentHtml={prototypeSlice.contentHtml}
      extraStyles={coachesExtraStyles}
      extraHeadLinks={prototypeSlice.extraHeadLinks}
      emptyMessage="Create or seed the coaches record in mc_v2_page_vertical."
    />
  )
}

export default CoachesPage

export const query = graphql`
  query CoachesCmsPageQuery {
    allDatoCmsMcV2PageVertical(filter: { slug: { eq: "coaches" } }, limit: 1) {
      nodes {
        seo {
          ...CmsSeoFields
        }
        slug
        eyebrow
        heading
        summary
        body
        # bodyHeading  # TODO(T-014 add the Dato field, T-015 then uncomment): blocked on DatoCMS schema
        heroMediaType
        heroImageUrl
        heroImageAlt
        heroVideoUrl
        heroVideoPosterUrl
        contentHtml
        sectionCollection {
          contentSections {
            ...CmsHtmlSectionFields
          }
        }
        proofCollection {
          sourceCollection {
            key
          }
          selectedProfileEntries {
            quote
            name
            initials
            role
            industry
            stat
            logoSvg
            gradient
          }
          selectedTestimonialEntries {
            quote
            customerName
            customerInitials
            customerRole
            industry
            brandName
            metaOne
            metaTwo
            metaThree
            statOneValue
            statOneLabel
            statTwoValue
            statTwoLabel
            statThreeValue
            statThreeLabel
          }
          testimonialEntries {
            quote
            customerName
            customerInitials
            customerRole
            industry
            brandName
            metaOne
            metaTwo
            metaThree
            statOneValue
            statOneLabel
            statTwoValue
            statTwoLabel
            statThreeValue
            statThreeLabel
          }
        }
        faqCollection {
          sourceCollection {
            key
          }
          selectedFaqEntries {
            question
            answer
          }
        }
      }
    }
    allDatoCmsMcV2SectionCollection {
      nodes {
        key
        contentSections {
          ...CmsHtmlSectionFields
        }
      }
    }
    allDatoCmsMcV2ProofCollection {
      nodes {
        key
        sourceCollection {
          key
        }
        selectedProfileEntries {
          quote
          name
          initials
          role
          industry
          stat
          logoSvg
          gradient
        }
        selectedTestimonialEntries {
          quote
          customerName
          customerInitials
          customerRole
          industry
          brandName
          metaOne
          metaTwo
          metaThree
          statOneValue
          statOneLabel
          statTwoValue
          statTwoLabel
          statThreeValue
          statThreeLabel
        }
        testimonialEntries {
          quote
          customerName
          customerInitials
          customerRole
          industry
          brandName
          metaOne
          metaTwo
          metaThree
          statOneValue
          statOneLabel
          statTwoValue
          statTwoLabel
          statThreeValue
          statThreeLabel
        }
      }
    }
    allDatoCmsMcV2FaqCollection {
      nodes {
        key
        sourceCollection {
          key
        }
        selectedFaqEntries {
          question
          answer
        }
      }
    }
  }
`
